import { useMemo } from 'react';
import { useReducedMotion } from 'motion/react';
import { getStaggerDelay, listStagger } from '@/shared/constants/motion.constants';

/**
 * DOM `onAnimationStart` / drag-event handlers collide with motion's
 * same-named props (motion redefines them with its own signatures), so a
 * motion element's passthrough surface must drop them.
 */
export type StripMotionCollisions<T> = Omit<
  T,
  'onAnimationStart' | 'onAnimationEnd' | 'onDrag' | 'onDragStart' | 'onDragEnd'
>;

/**
 * Shared staggered-entrance timing for list/grid items. Returns whether motion
 * is suppressed and the transition to hand a motion element — an item at
 * `index` animates one capped `listStagger` step after the previous one.
 *
 * The transition is memoized on `index` so a re-render doesn't hand motion a
 * fresh identity and restart the tween. Callers gate on `shouldReduceMotion`
 * to render a plain, static element.
 */
export function useStaggerEntrance(index: number) {
  const shouldReduceMotion = useReducedMotion();

  const transition = useMemo(
    () => ({
      duration: listStagger.durationS,
      ease: 'easeOut' as const,
      delay: getStaggerDelay(index),
    }),
    [index],
  );

  return { shouldReduceMotion, transition };
}
